import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { doDelete, doGet, doPost, reportingOrigin, setLocalStorage } from 'lib/utils';
import { initializeAnalytics, trackError } from 'lib/analytics';
import { FullStory } from '@fullstory/browser';
import {
  HoneAbility,
  HoneAbilityActions,
  HoneAbilitySubjects,
  HoneUserRoles,
  defineAbilityFor,
} from '@hone-automation/common';
import { useQuery } from '@tanstack/react-query';
import { makeRemoteGetLocationsUsers } from 'main/factories/usecases';
import { useLocationsStore } from '../hooks/useLocationsStore';
import { useHoneGetUserInfo } from '../hooks/useHoneGetUserInfo';

// Types
export interface User {
  uid: string;
  email: string;
  displayName?: string;
}

interface LoginDto {
  username: string;
  password: string;
}

interface PasswordResetDto {
  email: string;
}

interface AuthContextType {
  loading: boolean;
  user: User | null;
  session: string | null;
  userId: string | null;
  status: string;
  hasAdminRole: boolean;
  isBookkeeperAdmin: boolean;
  userInfo: HoneLocationUser;
  currentLocation: HoneLocationUser | undefined;
  allLocationUsers: HoneLocationUser[];
  isOwner: boolean;
  userInfoHasLoaded: boolean;
  userLocationsForUser: Record<string, HoneLocationUser>;
  currentLocationAbility: any;
  currentLocationAbilities: HoneAbility[];
  isInternalUser: boolean;

  // Auth functions
  signOut: () => Promise<void>;
  login: ({ username, password }: LoginDto) => Promise<string>;
  sendPasswordResetEmail: ({ email }: PasswordResetDto) => Promise<string>;
  verifyPasswordResetCode: (oobCode: string) => Promise<{ status: string; email?: string; error?: string }>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<{ status: string; error?: string }>;

  // User management functions
  setAllLocationUsers: (allLocationUsers: HoneLocationUser[]) => void;
  updateLocationUser: (email: string, role: string, emailOptOut: boolean, locationId?: string) => Promise<any>;
  removeLocationUser: (email: string, locationId?: string) => Promise<any>;
}

const isTest = process.env.NODE_ENV === 'test';
const locationUsers = makeRemoteGetLocationsUsers();

// Create context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to get location users data
export function useHoneGetUsers(locationId: string | undefined, user: User | null) {
  return useQuery({
    enabled: !!locationId && !!user && !!user.uid && !!localStorage.getItem('hone:session'),
    queryKey: ['locationsUsers', locationId, user?.uid],
    queryFn: async () => {
      if (!user) return [];
      return (await locationUsers.getAll({ userId: user.uid, locationId })) as HoneLocationUser[];
    },
  });
}

// Custom hook to get user locations
function useUserLocationsQuery(user: User | null) {
  return useQuery({
    queryKey: ['userLocations', user?.uid],
    enabled: !!user && !!user.uid,
    queryFn: async () => {
      if (!user) return [];
      const response = await doGet(`${reportingOrigin()}/userLocations?userId=${user.uid}`);
      return response.data;
    },
  });
}

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  // Auth state
  const [loading, setLoading] = useState<boolean>(false);
  const [session, setSession] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<'pending' | 'success'>('pending');
  // Location users state
  const currentLocationId = useLocationsStore(state => state.currentLocationId);
  const [allLocationUsers, setAllLocationUsers] = useState<HoneLocationUser[]>([]);
  const [userLocationsForUser, setUserLocationsForUser] = useState<Record<string, HoneLocationUser>>({});
  const [currentLocation, setCurrentLocation] = useState<HoneLocationUser | undefined>();
  const [currentLocationAbility, setCurrentLocationAbility] = useState<any>([]);
  const [currentLocationAbilities, setCurrentLocationAbilities] = useState<HoneAbility[]>([]);

  // Data fetching
  const { data: userInfo, isSuccess: userInfoHasLoaded } = useHoneGetUserInfo(user ? user.uid : '', currentLocationId);
  const { data: userLocations } = useUserLocationsQuery(user);

  // Derived state
  const userIsAdmin = (userInfo as HoneLocationUser)?.role === HoneUserRoles.Bookkeeper_Account_Admin;

  let isOwner = false;
  if (user && currentLocationId) {
    isOwner = userIsAdmin ? true : userInfo ? (userInfo as HoneLocationUser).role === HoneUserRoles.Owner : false;
  }

  // Authentication functions
  const login = async ({ username, password }: LoginDto) => {
    return await doPost(`${reportingOrigin()}/login`, { username, password })
      .then(result => {
        if (result && result.data.status === 'success') {
          const userData = result.data.firebaseUser;
          setUser(userData);
          setSession(result.data.session);
          setLocalStorage('hone:signedParamsCDN', result.data.signedParamsCDN);
          setLocalStorage('hone:session', result.data.session);

          initializeAnalytics(userData);

          if (!isTest) {
            FullStory('setProperties', {
              type: 'user',
              properties: {
                displayName: userData.displayName ? userData.displayName : '',
                email: userData.email ? userData.email : '',
              },
            });
          }
        }
        return 'success';
      })
      .catch(() => {
        return 'error';
      });
  };

  const sendPasswordResetEmail = async ({ email }: PasswordResetDto) => {
    return await doPost(`${reportingOrigin()}/users/passwordResetEmail`, { email })
      .then(() => 'success')
      .catch(() => 'error');
  };

  const verifyPasswordResetCode = async (oobCode: string) => {
    try {
      const response = await doPost(`${reportingOrigin()}/users/verifyPasswordResetCode`, { oobCode });
      return response.data;
    } catch (error) {
      return { status: 'error', error: 'auth/invalid-action-code' };
    }
  };

  const confirmPasswordReset = async (oobCode: string, newPassword: string) => {
    try {
      const response = await doPost(`${reportingOrigin()}/users/confirmPasswordReset`, {
        oobCode,
        newPassword,
      });
      return response.data;
    } catch (error) {
      return { status: 'error', error: 'generic' };
    }
  };

  const deleteSession = () => {
    localStorage.removeItem('hone:session');
    localStorage.removeItem('hone:signedParamsCDN');
    localStorage.removeItem('hone:locationId');
    setUser(null);
    setSession(null);
  };

  const signOut = async () => {
    deleteSession();
  };

  const removeLocationUser = async (email: string, locationId?: string) => {
    const location = locationId ?? currentLocationId;
    try {
      return doDelete(
        `${reportingOrigin()}/removeLocationUser?userId=${user?.uid}&locationId=${location}&email=${email}`
      );
    } catch (error) {
      trackError({ error: error as Error });
      return { error: error };
    }
  };

  // Initialize auth
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      if (!localStorage.getItem('hone:session')) return;
      setLoading(true);
      try {
        const response = await doGet(`${reportingOrigin()}/users/firebaseUser`);
        setUser(response.data);
        setSession(localStorage.getItem('hone:session'));
        initializeAnalytics(response.data);
      } catch (e) {
        deleteSession();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Update location users data
  useEffect(() => {
    if (!user?.uid) {
      setCurrentLocation(undefined);
      setCurrentLocationAbilities([]);
      setCurrentLocationAbility([]);
      setAllLocationUsers([]);
      setUserLocationsForUser({});
      return;
    }

    if (!userLocations || !currentLocationId) return;

    // Convert to record map for easier data access
    const ulMap: Record<string, HoneLocationUser> = {};
    (userLocations as HoneLocationUser[]).forEach((ul: HoneLocationUser) => (ulMap[ul.locationId] = ul));
    setUserLocationsForUser(ulMap);

    // Set current location permissions
    const currentUl = ulMap[currentLocationId];
    if (currentUl) {
      setCurrentLocation(currentUl);
      if (currentUl.abilities) {
        const abilities = currentUl.abilities ?? [];
        setCurrentLocationAbilities(abilities);
        setCurrentLocationAbility(defineAbilityFor(abilities));
        setStatus('success');
      }
    }
  }, [user, userLocations, currentLocationId]);

  return (
    <AuthContext.Provider
      value={{
        // Auth state
        loading,
        user,
        userId: user?.uid ?? null,
        session,
        userInfo,

        // Location users state
        status,
        isBookkeeperAdmin: currentLocation?.role === HoneUserRoles.Bookkeeper_Account_Admin,
        allLocationUsers,
        isOwner,
        hasAdminRole: userIsAdmin,
        userInfoHasLoaded,
        currentLocation,
        userLocationsForUser,
        currentLocationAbility,
        currentLocationAbilities,
        // @alias hasAdminRole
        isInternalUser: userIsAdmin,

        // Auth functions
        login,
        signOut,
        sendPasswordResetEmail,
        verifyPasswordResetCode,
        confirmPasswordReset,

        // User management functions
        setAllLocationUsers,
        removeLocationUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// For backward compatibility
export const useAuthContext = useAuth;
export const useHoneLocationUsers = useAuth;
